import { TABLE_PICKER_CONFIG } from '@modules/global/constants/MarkdownField.js'

/**
 * Normalize URLs by ensuring http(s) protocol for plain domains.
 * @param {string} url
 * @returns {string}
 */
export function normalizeUrl(url) {
  const trimmed = String(url || '').trim()
  if (!trimmed) return ''
  if (/^(https?:\/\/|mailto:)/i.test(trimmed)) return trimmed
  return 'https://' + trimmed
}

// =============================================================================
// SELECTION UTILITIES
// =============================================================================

function getSelection(win) {
  if (!win || typeof win.getSelection !== 'function') return null
  return win.getSelection()
}

function isRangeInEditor(range, editor) {
  if (!range || !editor) return false
  return editor.contains(range.commonAncestorContainer)
}

/**
 * Ensure the current selection is inside the editor.
 * @param {{ deps: { editor: HTMLElement | null, window: Window, document: Document } }} params
 * @returns {void}
 */
export function ensureSelectionInEditor({ deps }) {
  const { editor, window, document } = deps || {}
  if (!editor || !document) return

  const selection = getSelection(window)
  if (!selection) return

  const hasRange = selection.rangeCount > 0
  const range = hasRange ? selection.getRangeAt(0) : null

  if (range && isRangeInEditor(range, editor)) return

  const newRange = document.createRange()
  newRange.selectNodeContents(editor)
  newRange.collapse(false)
  selection.removeAllRanges()
  selection.addRange(newRange)
}

/**
 * Save the current selection range when it is inside the editor.
 * @param {{ deps: { editor: HTMLElement | null, window: Window } }} params
 * @returns {Range | null}
 */
export function saveSelectionRange({ deps }) {
  const { editor, window } = deps || {}
  if (!editor) return null

  const selection = getSelection(window)
  if (!selection || selection.rangeCount === 0) return null

  const range = selection.getRangeAt(0)
  return isRangeInEditor(range, editor) ? range.cloneRange() : null
}

// =============================================================================
// DOM AND TEXT MANIPULATION SERVICE
// =============================================================================

/**
 * Create a document fragment from text with line breaks
 * @param {string} text - Text to convert
 * @returns {DocumentFragment}
 */
export function createTextFragment(text) {
  const fragment = document.createDocumentFragment()
  const textString = String(text || '')
  const lines = textString.split('\n')

  lines.forEach((line, index) => {
    fragment.appendChild(document.createTextNode(line))

    const isNotLastLine = index < lines.length - 1
    if (isNotLastLine) {
      fragment.appendChild(document.createElement('br'))
    }
  })

  return fragment
}

/**
 * Move caret to the end of a node
 * @param {Node} node - Target node
 */
export function moveCaretToEnd(node) {
  if (!node) return
  const selection = getSelection(window)
  if (!selection) return

  if (node.nodeType === Node.TEXT_NODE) {
    moveCaretToTextNodeEnd(selection, node)
    return
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    moveCaretToElementEnd(selection, node)
  }
}

function moveCaretToTextNodeEnd(selection, node) {
  const range = document.createRange()
  const maxOffset = Math.min(node.length, node.length)
  range.setStart(node, maxOffset)
  range.setEnd(node, maxOffset)
  selection.removeAllRanges()
  selection.addRange(range)
}

function moveCaretToElementEnd(selection, node) {
  const range = document.createRange()
  range.selectNodeContents(node)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
}

function tryInsertTextWithUndo(text) {
  if (!document || typeof document.execCommand !== 'function') {
    return false
  }
  try {
    return document.execCommand('insertText', false, text)
  } catch (error) {
    return false
  }
}

function moveCaretRelative(offset) {
  const selection = window.getSelection && window.getSelection()
  if (!selection || selection.rangeCount === 0) {
    return
  }

  const range = selection.getRangeAt(0)
  const node = range.startContainer

  if (!node || node.nodeType !== Node.TEXT_NODE) {
    return
  }

  const nextOffset = Math.max(0, Math.min(node.length, range.startOffset + offset))
  range.setStart(node, nextOffset)
  range.setEnd(node, nextOffset)
  selection.removeAllRanges()
  selection.addRange(range)
}

/**
 * Set caret position in a text node
 * @param {Node} node - Text node
 * @param {number} offset - Caret offset
 */
export function setCaretInNode(node, offset) {
  if (!node) {
    return
  }

  const selection = window.getSelection && window.getSelection()
  if (!selection) {
    return
  }

  const range = document.createRange()
  const maxOffset = Math.min(offset, node.length)
  range.setStart(node, maxOffset)
  range.setEnd(node, maxOffset)
  selection.removeAllRanges()
  selection.addRange(range)
}

/**
 * Get selection range if it's inside the editor
 * @param {HTMLElement} editor - Editor element
 * @returns {Range | null}
 */
export function getSelectionRangeInEditor(editor) {
  const selection = window.getSelection && window.getSelection()
  if (!selection || selection.rangeCount === 0) {
    return null
  }

  const range = selection.getRangeAt(0)
  if (!editor) {
    return null
  }

  const isInEditor = editor.contains(range.commonAncestorContainer)
  return isInEditor ? range : null
}

/**
 * Insert text fragment at current selection
 * @param {Range} range - Selection range
 * @param {DocumentFragment} fragment - Fragment to insert
 * @returns {Node | null} - Last node inserted
 */
export function insertFragmentAtRange(range, fragment) {
  if (!range || !fragment) {
    return null
  }

  const lastNode = fragment.lastChild

  range.deleteContents()
  range.insertNode(fragment)

  return lastNode
}

/**
 * Get clean text from editor
 * @param {HTMLElement} editor - Editor element
 * @returns {string}
 */
export function getEditorText(editor) {
  if (!editor) {
    return ''
  }

  const text = editor.innerText || ''
  return text.replace(/\u00A0/g, ' ')
}

/**
 * Get plain text without zero-width characters
 * @param {string} value - Text value
 * @returns {string}
 */
export function getPlainText(value) {
  return String(value || '')
    .replace(/\u200B/g, '')
    .trim()
}

/**
 * Format height value to CSS string
 * @param {number | string} value
 * @returns {string}
 */
export function formatHeightValue(value) {
  if (typeof value === 'number') {
    return value + 'px'
  }
  return String(value)
}

// =============================================================================
// TOOLBAR ACTIONS SERVICE
// =============================================================================

/**
 * Execute toolbar action based on action type
 * @param {Object} params
 * @param {string} params.action - Action type
 * @param {Range} params.range - Selection range
 * @param {Function} params.insertText - Function to insert text
 * @param {Function} params.setCaretPosition - Function to set caret position
 * @returns {Object | null} - Result of action
 */
export function executeToolbarAction({
  action,
  range,
  insertText,
  setCaretPosition,
}) {
  const actionHandlers = {
    bold: () => handleWrapAction(range, '**', '**', setCaretPosition),
    italic: () => handleWrapAction(range, '*', '*', setCaretPosition),
    strike: () => handleWrapAction(range, '~~', '~~', setCaretPosition),
    code: () => handleWrapAction(range, '`', '`', setCaretPosition),
    codeBlock: () => handleCodeBlockAction(insertText),
    quote: () => handleLinePrefixAction(range, '>', insertText),
    h1: () => handleLinePrefixAction(range, '#', insertText),
    h2: () => handleLinePrefixAction(range, '##', insertText),
    h3: () => handleLinePrefixAction(range, '###', insertText),
    unordered: () => handleLinePrefixAction(range, '-', insertText),
    ordered: () => handleLinePrefixAction(range, '1.', insertText),
    link: () => handleLinkAction(range, insertText),
    image: () => handleImageAction(insertText),
  }

  const handler = actionHandlers[action]
  if (handler) {
    return handler()
  }

  return null
}

/**
 * Handle wrap actions (bold, italic, strike, code)
 * @param {Range} range
 * @param {string} prefix
 * @param {string} suffix
 * @param {Function} setCaretPosition
 * @returns {Object}
 */
function handleWrapAction(range, prefix, suffix, setCaretPosition) {
  const result = wrapTextWithMarkdown({ range, prefix, suffix })
  if (result) {
    setCaretPosition(result.node, result.caretOffset)
  }
  return result
}

/**
 * Handle line prefix actions (quote, headers, lists)
 * @param {Range} range
 * @param {string} prefix
 * @param {Function} insertText
 * @returns {Object}
 */
function handleLinePrefixAction(range, prefix, insertText) {
  if (!range) {
    return null
  }

  const selected = range.toString()

  if (!selected) {
    insertText(prefix + ' ')
    return { type: 'prefix', prefix }
  }

  const formattedText = formatLinesWithPrefix(selected, prefix)
  insertText(formattedText)
  return { type: 'prefix', prefix, formatted: formattedText }
}

/**
 * Handle code block action
 * @param {Function} insertText
 * @returns {Object}
 */
function handleCodeBlockAction(insertText) {
  const codeBlock = generateCodeBlock()
  insertText(codeBlock)
  return { type: 'codeBlock', text: codeBlock }
}

/**
 * Handle link insertion
 * @param {Range} range
 * @param {Function} insertText
 * @returns {Object | null}
 */
function handleLinkAction(range, insertText) {
  const url = window.prompt('Informe a URL do link')
  if (!url) {
    return null
  }

  const normalized = normalizeUrl(url)
  const selected = range ? range.toString() : ''
  const label = selected || 'link'
  const linkMarkdown = generateMarkdownLink(normalized, label)

  insertText(linkMarkdown)
  return { type: 'link', url: normalized, label, text: linkMarkdown }
}

/**
 * Handle image insertion
 * @param {Function} insertText
 * @returns {Object | null}
 */
function handleImageAction(insertText) {
  const url = window.prompt('Informe a URL da imagem')
  if (!url) {
    return null
  }

  const normalized = normalizeUrl(url)
  const alt = window.prompt('Informe o texto alternativo') || 'imagem'
  const imageMarkdown = generateMarkdownImage(normalized, alt)

  insertText(imageMarkdown)
  return { type: 'image', url: normalized, alt, text: imageMarkdown }
}

/**
 * Handle table insertion from picker
 * @param {number} rows
 * @param {number} cols
 * @param {Function} insertText
 * @returns {Object}
 */
export function handleTableInsertion(rows, cols, insertText) {
  const tableMarkdown = generateMarkdownTable(rows, cols)
  insertText(tableMarkdown)
  return { type: 'table', rows, cols, text: tableMarkdown }
}

// =============================================================================
// MARKDOWN FORMATTING SERVICE
// =============================================================================

/**
 * Wrap selected text with markdown prefix and suffix (bold, italic, etc.)
 * @param {Object} params
 * @param {Range} params.range - Selection range
 * @param {string} params.prefix - Markdown prefix
 * @param {string} params.suffix - Markdown suffix
 * @returns {Object} { node, caretOffset }
 */
export function wrapTextWithMarkdown({ range, prefix, suffix }) {
  if (!range) {
    return null
  }

  const selected = range.toString()
  const text = selected ? prefix + selected + suffix : prefix + suffix
  if (tryInsertTextWithUndo(text)) {
    if (!selected) {
      moveCaretRelative(-suffix.length)
    }
    const caretOffset = selected ? text.length : prefix.length
    return { node: null, caretOffset }
  }

  const node = document.createTextNode(text)
  range.deleteContents()
  range.insertNode(node)

  const caretOffset = selected ? text.length : prefix.length
  return { node, caretOffset }
}

/**
 * Format lines with a prefix (quotes, headers, lists)
 * @param {string} text - Text to format
 * @param {string} prefix - Markdown prefix (>, #, -, 1., etc.)
 * @returns {string}
 */
export function formatLinesWithPrefix(text, prefix) {
  const lines = text.split('\n')
  let counter = 1

  return lines
    .map((line) => {
      if (prefix === '1.') {
        const result = counter + '. ' + line
        counter += 1
        return result
      }
      return prefix + ' ' + line
    })
    .join('\n')
}

/**
 * Generate markdown code block
 * @returns {string}
 */
export function generateCodeBlock() {
  return '```\n\n```'
}

/**
 * Generate markdown link
 * @param {string} url - Link URL
 * @param {string} label - Link label
 * @returns {string}
 */
export function generateMarkdownLink(url, label) {
  const linkLabel = label || 'link'
  return '[' + linkLabel + '](' + url + ')'
}

/**
 * Generate markdown image
 * @param {string} url - Image URL
 * @param {string} alt - Alternative text
 * @returns {string}
 */
export function generateMarkdownImage(url, alt) {
  const altText = alt || 'imagem'
  return '![' + altText + '](' + url + ')'
}

// =============================================================================
// MARKDOWN TABLE GENERATION SERVICE
// =============================================================================

/**
 * Clamp a value between min and max
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clampValue(value, min, max) {
  const numValue = Number(value) || 0
  return Math.max(min, Math.min(max, numValue))
}

/**
 * Generate table header row
 * @param {number} cols - Number of columns
 * @returns {string}
 */
function generateTableHeader(cols) {
  const headers = []

  for (let i = 0; i < cols; i++) {
    headers.push('Col ' + (i + 1))
  }

  return '| ' + headers.join(' | ') + ' |'
}

/**
 * Generate table separator row
 * @param {number} cols - Number of columns
 * @returns {string}
 */
function generateTableSeparator(cols) {
  const separators = []

  for (let i = 0; i < cols; i++) {
    if (i === 0) {
      separators.push('-----')
    } else {
      separators.push(':---:')
    }
  }

  return '| ' + separators.join(' | ') + ' |'
}

/**
 * Generate default cell content
 * @returns {string}
 */
function generateTableCell() {
  return 'value'
}

/**
 * Generate table body rows
 * @param {number} rows - Number of rows
 * @param {number} cols - Number of columns
 * @returns {string[]}
 */
function generateTableBody(rows, cols) {
  const bodyLines = []

  for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
    const cells = []

    for (let colIndex = 0; colIndex < cols; colIndex++) {
      const cell = generateTableCell()
      cells.push(cell)
    }

    bodyLines.push('| ' + cells.join(' | ') + ' |')
  }

  return bodyLines
}

/**
 * Generate complete markdown table
 * @param {number} rows - Number of rows
 * @param {number} cols - Number of columns
 * @returns {string}
 */
export function generateMarkdownTable(rows, cols) {
  const safeRows = clampValue(
    rows,
    TABLE_PICKER_CONFIG.MIN_ROWS,
    TABLE_PICKER_CONFIG.MAX_ROWS
  )
  const safeCols = clampValue(
    cols,
    TABLE_PICKER_CONFIG.MIN_COLS,
    TABLE_PICKER_CONFIG.MAX_COLS
  )

  const header = generateTableHeader(safeCols)
  const separator = generateTableSeparator(safeCols)
  const body = generateTableBody(safeRows, safeCols)

  const lines = [header, separator, ...body, '']
  return lines.join('\n')
}
